import { useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { authAPI } from "../lib/api";
import { Archive, Loader2, CheckCircle2, Check, X } from "lucide-react";

function checkPassword(pw) {
  return {
    length: (pw || "").length >= 8,
    upper: /[A-Z]/.test(pw || ""),
    lower: /[a-z]/.test(pw || ""),
    digit: /\d/.test(pw || ""),
    symbol: /[^A-Za-z0-9]/.test(pw || ""),
  };
}

function isStrong(pw) {
  const c = checkPassword(pw);
  return c.length && c.upper && c.lower && c.digit && c.symbol;
}

function PasswordChecklist({ password }) {
  const checks = checkPassword(password);
  const items = [
    { key: "length", label: "At least 8 characters" },
    { key: "upper", label: "One uppercase letter" },
    { key: "lower", label: "One lowercase letter" },
    { key: "digit", label: "One number" },
    { key: "symbol", label: "One symbol (!@#$ etc.)" },
  ];
  return (
    <ul className="space-y-1 text-xs">
      {items.map((it) => {
        const ok = checks[it.key];
        return (
          <li key={it.key} className={`flex items-center gap-1.5 ${ok ? "text-green-600" : "text-muted-foreground"}`}>
            {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            {it.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function ResetPasswordPage({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const strong = useMemo(() => isStrong(password), [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!strong) {
      setError("Please meet all password requirements.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, password);
      if (res.data?.token && res.data?.user) {
        localStorage.setItem("archiva_token", res.data.token);
        localStorage.setItem("archiva_user", JSON.stringify(res.data.user));
      }
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg).join(" "));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Reset failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Archive className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight">Archiva</h1>
          </div>
        </div>
        <Card className="border border-border shadow-none">
          <CardContent className="pt-6">
            {done ? (
              <div className="text-center space-y-4 py-4">
                <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold">Password updated</h2>
                <p className="text-sm text-muted-foreground">You're now signed in with your new password.</p>
                <Button className="w-full" onClick={onDone}>Continue to Archiva</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-xl font-semibold text-center">Set a new password</h2>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  {password && (
                    <div className="rounded-md bg-gray-50 p-2">
                      <PasswordChecklist password={password} />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Re-enter new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
                )}
                <Button type="submit" className="w-full" disabled={loading || !strong || password !== confirm}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset password
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
